// shell/components/entity-layout/entity-layout.ts

import { IDisposable } from '../types';
import { rowSizeClass, ROW_SIZE_CLASSES } from './row-size';
import { hiddenLayoutItems } from './group-visibility';
import type { LayoutItemState } from './group-visibility';
import { DisposableStore } from '../disposable';
import { createElement, clearElement } from '../../utils/dom';
import type {
    LayoutDef,
    LayoutElement,
    LayoutHeader,
    LayoutCustom,
    LayoutSection,
    LayoutRow,
} from '../entity-view/types';
import {
    isLayoutHeader,
    isLayoutCustom,
    isLayoutSection,
    isLayoutRow,
} from '../entity-view/types';

// ============================================================================
// Types
// ============================================================================

/**
 * Context for rendering a field.
 */
export interface FieldRenderContext<T> {
    /** The field key */
    key: string;
    /** The field value */
    value: unknown;
    /** The full model */
    model: T;
    /** The container to render into */
    container: HTMLElement;
}

/**
 * Field renderer function signature.
 * Returns a disposable if cleanup is needed.
 */
export type FieldRenderer<T> = (ctx: FieldRenderContext<T>) => IDisposable | void;

/**
 * Configuration for EntityLayout.
 */
export interface EntityLayoutConfig<T> {
    /** Container element to render into */
    container: HTMLElement;
    /** Layout definition */
    layout: LayoutDef<T>;
    /** Callback to render individual fields */
    renderField: FieldRenderer<T>;
    /** ARIA label for accessibility */
    ariaLabel?: string;
}

// ============================================================================
// EntityLayout Class
// ============================================================================

/**
 * Renders a grid-based layout with headers, rows, and custom elements.
 *
 * The layout is responsible for structure only - field rendering is delegated
 * to the renderField callback.
 *
 * @template T - The entity model type
 */
export class EntityLayout<T> implements IDisposable {
    private readonly config: EntityLayoutConfig<T>;
    private readonly disposables = new DisposableStore();
    private readonly fieldDisposables = new Map<string, IDisposable>();
    private readonly rootElement: HTMLElement;
    private model: T | null = null;

    constructor(config: EntityLayoutConfig<T>) {
        this.config = config;

        this.rootElement = createElement('div', {
            className: 'el-layout',
            attributes: config.ariaLabel ? { 'aria-label': config.ariaLabel } : {},
        });

        config.container.appendChild(this.rootElement);
    }

    /**
     * Render the layout with the given model.
     */
    render(model: T): void {
        this.model = model;
        this.disposeFields();
        clearElement(this.rootElement);

        for (const element of this.config.layout) {
            const rendered = this.renderElement(element);
            if (rendered) {
                this.rootElement.appendChild(rendered);
            }
        }

        // Rows and headers left with nothing shown (group-visibility.ts).
        this.applyGroupVisibility(this.rootElement);
    }

    /**
     * Render a loading state placeholder.
     */
    renderLoading(): void {
        this.disposeFields();
        clearElement(this.rootElement);

        const loading = createElement('div', {
            className: 'el-loading',
        });

        // Create placeholder rows
        for (let i = 0; i < 5; i++) {
            const row = createElement('div', {
                className: 'el-loading-row',
            });

            const label = createElement('div', {
                className: 'el-loading-label el-skeleton',
            });
            row.appendChild(label);

            const value = createElement('div', {
                className: 'el-loading-value el-skeleton',
            });
            row.appendChild(value);

            loading.appendChild(row);
        }

        this.rootElement.appendChild(loading);
    }

    /**
     * Render an error state.
     */
    renderError(error: Error): void {
        this.disposeFields();
        clearElement(this.rootElement);

        const errorEl = createElement('div', {
            className: 'el-error',
        });

        const icon = createElement('div', {
            className: 'el-error-icon',
            textContent: '!',
        });
        errorEl.appendChild(icon);

        const message = createElement('div', {
            className: 'el-error-message',
            textContent: error.message || 'An error occurred',
        });
        errorEl.appendChild(message);

        this.rootElement.appendChild(errorEl);
    }

    /**
     * Get a field container by key for direct updates.
     */
    getFieldContainer(key: string): HTMLElement | null {
        return this.rootElement.querySelector(`[data-field-key="${key}"]`);
    }

    dispose(): void {
        this.disposeFields();
        clearElement(this.rootElement);
        this.rootElement.remove();
        this.disposables.dispose();
    }

    // =========================================================================
    // Private: Rendering
    // =========================================================================

    private renderElement(element: LayoutElement<T>): HTMLElement | null {
        if (isLayoutSection(element)) {
            return this.renderSection(element);
        }

        if (isLayoutHeader(element)) {
            return this.renderHeader(element);
        }

        if (isLayoutCustom(element)) {
            return this.renderCustom(element);
        }

        if (isLayoutRow(element)) {
            return this.renderRow(element);
        }

        return null;
    }

    private renderSection(section: LayoutSection<T>): HTMLElement {
        const el = createElement('div', {
            className: `el-section ${section.collapsed ? 'el-section--collapsed' : ''}`,
        });

        const toggle = createElement('button', {
            className: 'el-section-toggle',
            attributes: {
                type: 'button',
                'aria-expanded': section.collapsed ? 'false' : 'true',
            },
        });

        const arrow = createElement('span', {
            className: 'el-section-arrow',
            textContent: '\u25B6', // ▶
        });
        toggle.appendChild(arrow);

        const text = createElement('span', {
            className: 'el-section-text',
            textContent: section.section,
        });
        toggle.appendChild(text);

        el.appendChild(toggle);

        const body = createElement('div', {
            className: 'el-section-body',
        });

        for (const child of section.children) {
            const rendered = this.renderElement(child);
            if (rendered) {
                body.appendChild(rendered);
            }
        }

        el.appendChild(body);

        toggle.addEventListener('click', () => {
            const collapsed = el.classList.toggle('el-section--collapsed');
            toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
        });

        return el;
    }

    private renderHeader(header: LayoutHeader): HTMLElement {
        const el = createElement('div', {
            className: 'el-header',
        });

        if (header.icon) {
            const icon = createElement('span', {
                className: `el-header-icon el-icon-${header.icon}`,
                attributes: { 'aria-hidden': 'true' },
            });
            el.appendChild(icon);
        }

        const text = createElement('span', {
            className: 'el-header-text',
            textContent: header.header,
        });
        el.appendChild(text);

        return el;
    }

    private renderCustom(custom: LayoutCustom<T>): HTMLElement {
        const el = createElement('div', {
            className: 'el-custom',
        });

        if (this.model) {
            try {
                custom.custom(el, this.model);
            } catch (error) {
                console.error('Error rendering custom layout element:', error);
                el.classList.add('el-custom--error');
                el.textContent = 'Error rendering custom content';
            }
        }

        return el;
    }

    private renderRow(row: LayoutRow<T>): HTMLElement {
        const el = createElement('div', {
            className: 'el-row',
        });

        for (const fieldKey of row) {
            const field = this.renderFieldWrapper(String(fieldKey));
            el.appendChild(field);
        }

        // Sized by the fields actually shown: renderField has already hidden
        // (display: none) any field whose `visible` predicate is false, and a
        // hidden field must not leave a hole beside the one that remains.
        this.applyRowSize(el);

        return el;
    }

    /**
     * Re-size every row by its currently shown fields, and hide or show again
     * each row and header by whether anything under it is shown — call after
     * toggling field visibility in place (EntityView's form-mode re-evaluation).
     */
    refreshRowSizes(): void {
        for (const row of Array.from(this.rootElement.querySelectorAll<HTMLElement>('.el-row'))) {
            this.applyRowSize(row);
        }
        this.applyGroupVisibility(this.rootElement);
    }

    /**
     * Hides a row whose every field is hidden, and a header whose group holds
     * only such rows (`hiddenLayoutItems`); shows them again otherwise. Runs
     * over one level of layout items — the root, or a section's body — and
     * recurses into sections.
     */
    private applyGroupVisibility(container: HTMLElement): void {
        const children = Array.from(container.children) as HTMLElement[];
        const items = children.map((child): LayoutItemState => {
            if (child.classList.contains('el-header')) return { kind: 'header' };
            if (child.classList.contains('el-row')) {
                const fields = Array.from(child.children) as HTMLElement[];
                return { kind: 'row', fields: fields.length, shown: fields.filter(f => f.style.display !== 'none').length };
            }
            return { kind: 'other' };
        });
        const hidden = hiddenLayoutItems(items);
        children.forEach((child, i) => {
            if (items[i].kind !== 'other') child.style.display = hidden[i] ? 'none' : '';
            if (child.classList.contains('el-section')) {
                for (const body of Array.from(child.children) as HTMLElement[]) {
                    if (body.classList.contains('el-section-body')) this.applyGroupVisibility(body);
                }
            }
        });
    }

    private applyRowSize(row: HTMLElement): void {
        const shown = Array.from(row.children)
            .filter(child => (child as HTMLElement).style.display !== 'none').length;
        row.classList.remove(...ROW_SIZE_CLASSES);
        row.classList.add(rowSizeClass(shown));
    }

    private renderFieldWrapper(key: string): HTMLElement {
        const wrapper = createElement('div', {
            className: 'el-field',
            attributes: { 'data-field-key': key },
        });

        // Delegate rendering to the callback
        if (this.model) {
            const value = (this.model as Record<string, unknown>)[key];

            const ctx: FieldRenderContext<T> = {
                key,
                value,
                model: this.model,
                container: wrapper,
            };

            const disposable = this.config.renderField(ctx);
            if (disposable) {
                this.fieldDisposables.set(key, disposable);
            }
        }

        return wrapper;
    }

    private disposeFields(): void {
        for (const disposable of this.fieldDisposables.values()) {
            disposable.dispose();
        }
        this.fieldDisposables.clear();
    }
}

// ============================================================================
// Field Placeholder Renderer
// ============================================================================

/**
 * A simple placeholder renderer for development/testing.
 * Shows field key and value.
 */
export function createPlaceholderRenderer<T>(): FieldRenderer<T> {
    return (ctx: FieldRenderContext<T>) => {
        const label = createElement('div', {
            className: 'el-field-label',
            textContent: ctx.key,
        });
        ctx.container.appendChild(label);

        const value = createElement('div', {
            className: 'el-field-value',
            textContent: formatValue(ctx.value),
        });
        ctx.container.appendChild(value);
    };
}

function formatValue(value: unknown): string {
    if (value === null || value === undefined) {
        return '—';
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return String(value);
}
